import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Loader2, CheckCircle } from 'lucide-react';
import { LOGO_FULL_FALLBACK } from './constants';

export function ForgotPasswordPage() {
  const { api } = useAuth();
  const { logoUrl } = useSiteConfig();
  const LOGO_FULL = logoUrl || LOGO_FULL_FALLBACK;
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <CheckCircle className="w-16 h-16 text-[#00E676] mx-auto mb-6" />
          <h1 className="font-outfit text-2xl font-bold text-white mb-2">Check your email</h1>
          <p className="text-[#A0B4CC] mb-2">
            If an account exists for <span className="text-white">{email}</span>, we&apos;ve sent a password reset link to that address.
          </p>
          <p className="text-[#5C7599] text-sm mb-6">
            The link will expire in 1 hour. Don&apos;t see the email? Check your spam folder.
          </p>
          <div className="mt-2">
            <Link to="/login">
              <Button variant="ghost" className="text-[#A0B4CC]" data-testid="back-to-login-btn">Back to Login</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#000B18] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_FULL} alt="All Global Access" className="h-16 mx-auto mb-6" />
          </Link>
          <h1 className="font-outfit text-3xl font-bold text-white mb-2">Forgot Password</h1>
          <p className="text-[#A0B4CC]">Enter your email to reset your password</p>
        </div>

        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-md bg-[#FF1744]/10 border border-[#FF1744]/30 text-[#FF1744] text-sm">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#000B18] border-white/20 text-white placeholder:text-[#5C7599] focus:border-[#00E5FF]"
                  data-testid="forgot-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full btn-primary h-11"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link to="/login" className="text-[#00E5FF] hover:underline text-sm">
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


